import { useEffect, useState } from "react";
import { AdminShell, Card, Notice, Loading, confirmDanger } from "./AdminShell";
import { adminGetReleases, adminSetReleases, type AdminRelease } from "../../sim/admin";
import type { ReleaseState } from "../../sim/releases";

/**
 * ADMIN — RELEASES (docs/RELEASES.md). Where an update or a content package is switched on for players. Written for someone
 * who hasn't opened it in a month: each release is one card that says, in a sentence, what players see right now and
 * what is in it, and offers three plain choices —
 *   • 🔒 Hidden — players can't see or get any of it;
 *   • 👀 Coming soon — players see it LOCKED in the shop, the Market and the Field Guide, marked "Coming soon", and
 *     can't buy, plant or catch it;
 *   • ✅ Out now — every player gets it within a minute,
 * plus ⏰ "Turn on at…" a date and time, which the SERVER keeps: it switches on at that minute by itself, with nobody at
 * a PC and no deploy. Switching back to Hidden is the undo — players keep anything they already got.
 *
 * On the TEST server every release is already showing (that's what it's for); the "show it as live players see it"
 * switch hides it there too, to check the hidden state. Saving on LIVE asks first when it makes something visible.
 */
export function AdminReleases() {
  return (
    <AdminShell section="releases">
      <ReleasesCard />
    </AdminShell>
  );
}

const WHEN = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" });
const toLocalInput = (ms: number) => {
  const d = new Date(ms), p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
function inWords(ms: number, now: number): string {
  const d = Math.round((ms - now) / 60_000);
  if (d <= 0) return "now";
  if (d < 60) return `in ${d} minute${d === 1 ? "" : "s"}`;
  if (d < 48 * 60) return `in ${Math.round(d / 60)} hours`;
  return `in ${Math.round(d / 1440)} days`;
}
/** What players see, as one sentence. */
function statusLine(r: AdminRelease, st: ReleaseState | null, env: string, now: number): string {
  if (env === "staging" && !st?.simulate) return "🧪 Showing here — the test server always shows every release. Its state below is what LIVE players get.";
  if (st?.at != null && st.at > now && st.mode !== "on") {
    return `⏰ Turns on by itself ${WHEN.format(st.at)} (${inWords(st.at, now)}). Until then: ${st.mode === "preview" ? "coming soon" : "hidden"}.`;
  }
  const v = r.view;
  return v === "on" ? "✅ Out — players have it." : v === "preview"
    ? "👀 Coming soon — players see it locked in the shop and Field Guide, but can't get it yet."
    : "🔒 Hidden — players can't see or get any of it.";
}
function contentLine(r: AdminRelease): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => { if (n) parts.push(`${n} ${n === 1 ? one : many}`); };
  add(r.content.plants.length, "plant", "plants");
  add(r.content.decor.length, "decoration", "decorations");
  add(r.content.fish.length, "fish", "fish");
  add(r.content.insects.length, "bug", "bugs");
  return parts.length ? `Contains ${parts.join(", ")}.` : "Nothing is tagged with it yet.";
}
/** Its What's New entries: they wait for the release and appear, dated, the moment it comes out. */
function notesLine(r: AdminRelease): string {
  const n = r.notes ?? 0;
  if (!n) return "📰 No patch notes are waiting for it.";
  const s = n === 1 ? "patch note" : "patch notes";
  return r.view === "on" ? `📰 Its ${n} ${s} are on What's New.` : `📰 ${n} ${s} will appear on What's New the moment it comes out.`;
}

function ReleasesCard() {
  const [data, setData] = useState<Awaited<ReturnType<typeof adminGetReleases>> | null>(null);
  const [states, setStates] = useState<Record<string, ReleaseState>>({});
  const [pickAt, setPickAt] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => adminGetReleases().then((d) => {
    setData(d);
    setStates(Object.fromEntries(d.releases.filter((r) => r.state).map((r) => [r.id, r.state!])));
  }).catch((e) => setMsg({ ok: false, text: String((e as Error).message || e) }));
  useEffect(() => { void load(); }, []);
  if (!data) return msg ? <Notice ok={false}>{msg.text}</Notice> : <Loading what="Loading releases" />;

  const live = data.env === "production";
  async function save(id: string, next: ReleaseState, what: string) {
    const r = data!.releases.find((x) => x.id === id)!;
    if (live && (next.mode === "on" || next.at != null || next.mode === "preview")
      && !confirmDanger(`${what} — ${r.title}, on the LIVE server?`, next.mode === "on" ? "Every player gets it within a minute."
        : next.at != null ? `It switches on by itself at ${WHEN.format(next.at)}, for every player.`
        : "Players see it locked in the shop and Field Guide, marked “Coming soon”, but can't get it.")) return;
    setBusy(true); setMsg(null);
    try {
      const all = { ...states, [id]: next };
      setStates(await adminSetReleases(all));
      setMsg({ ok: true, text: `${r.title}: saved. Open games follow within a minute.` });
      await load();
    } catch (e) { setMsg({ ok: false, text: String((e as Error).message || e) }); }
    finally { setBusy(false); }
  }

  return (
    <Card title="Releases" icon="🗓️">
      <p className="adm-muted">
        Each update or content package is listed here once Claude has declared it. Choose what players get. A scheduled time
        is kept by the server, so it switches on at that minute even if nobody is at a computer. Switching back to
        Hidden is the undo; players keep anything they already got.
      </p>
      {msg && <Notice ok={msg.ok}>{msg.text}</Notice>}
      {data.releases.length === 0 ? (
        <p className="adm-muted" data-testid="releases-empty">
          No releases yet. When you start a content package, Claude adds its release here (src/sim/releases.ts).
        </p>
      ) : data.releases.map((r) => {
        const st = states[r.id] ?? null;
        const mode = st?.mode ?? "off";
        return (
          <div key={r.id} className="rel-card" data-testid={`release-${r.id}`}>
            <div className="rel-head"><b>{r.title}</b> <span className="adm-muted">({r.id})</span></div>
            <p className="rel-status">{statusLine(r, st, data.env, data.now)}</p>
            <p className="adm-muted">{contentLine(r)}</p>
            <p className="adm-muted" data-testid="release-notes">{notesLine(r)}</p>
            <div className="rel-choices">
              <button className={`site-btn sm${mode === "off" && st?.at == null ? " primary" : ""}`} disabled={busy}
                onClick={() => void save(r.id, { mode: "off", at: null, simulate: st?.simulate }, "Hide it")}>🔒 Hidden</button>
              <button className={`site-btn sm${mode === "preview" && st?.at == null ? " primary" : ""}`} disabled={busy}
                onClick={() => void save(r.id, { mode: "preview", at: null, simulate: st?.simulate }, "Show it as coming soon")}>👀 Coming soon</button>
              <button className={`site-btn sm${mode === "on" ? " primary" : ""}`} disabled={busy}
                onClick={() => void save(r.id, { mode: "on", at: null, simulate: st?.simulate }, "Release it now")}>✅ Out now</button>
            </div>
            <div className="rel-schedule">
              <label className="adm-muted">⏰ Turn on at
                <input className="adm-input" type="datetime-local" value={pickAt[r.id] ?? (st?.at ? toLocalInput(st.at) : "")}
                  onChange={(e) => setPickAt((p) => ({ ...p, [r.id]: e.target.value }))} />
              </label>
              <button className="site-btn sm" disabled={busy || !pickAt[r.id]}
                onClick={() => {
                  const at = new Date(pickAt[r.id]!).getTime();
                  if (!Number.isFinite(at)) return;
                  void save(r.id, { mode: mode === "on" ? "off" : mode, at, simulate: st?.simulate }, "Schedule it");
                }}>Schedule</button>
              <span className="adm-muted">(your computer's time zone; until then it stays {mode === "preview" ? "“coming soon”" : "hidden"})</span>
            </div>
            {data.env === "staging" && (
              <label className="adm-muted rel-sim">
                <input type="checkbox" checked={!!st?.simulate} disabled={busy}
                  onChange={(e) => void save(r.id, { mode, at: st?.at ?? null, simulate: e.target.checked }, "Change the test view")} />
                {" "}On this test server, show it the way live players see it (to check the hidden state)
              </label>
            )}
          </div>
        );
      })}
    </Card>
  );
}
